import { useState, useEffect, useMemo, useCallback } from 'react'
import type { QuestionnaireConfig, SingleQuestion, CrossValidationQuestion, Question, Answer, ContextType, TestResult } from '@mindtype/shared'
import { calculateResult } from '@mindtype/shared'
import { ResultPage } from './ResultPage'
import { SegmentedProgress } from './components/SegmentedProgress'
import { FrameworkIntro } from './components/FrameworkIntro'
import { saveResult, SaveResultResponse } from './services/api'
import { saveProgress, loadProgress, clearProgress, SavedProgress } from './utils/storage'

// 情境配置
const CONTEXT_INFO: Record<ContextType, { name: string; description: string }> = {
  natural: {
    name: '日常状态',
    description: '以下题目将探索您在日常放松状态下的自然反应和偏好。请回想您在没有外部压力时的真实想法。',
  },
  work: {
    name: '工作状态',
    description: '以下题目将探索您在工作或正式场合中的表现模式。请回想您在职业环境中的典型反应。',
  },
  stress: {
    name: '压力状态',
    description: '以下题目将探索您在压力或紧急情况下的应对方式。请回想您在面临挑战时的真实反应。',
  },
  mixed: {
    name: '综合情境',
    description: '以下题目将综合考察您在不同情境下的认知模式。请根据题目描述的具体情境作答。',
  }
}

// 页面状态类型
type PagePhase = 'framework' | 'intro' | 'personal_info' | 'context_transition' | 'question' | 'complete' | 'result'

// 个人信息类型
interface PersonalInfo {
  gender: string
  ageGroup: string
  currentStatus: string
  industryField: string
}

// Fisher-Yates 洗牌算法
function shuffleArray<T>(array: T[]): T[] {
  const result = [...array]
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[result[i], result[j]] = [result[j], result[i]]
  }
  return result
}

// 按情境分组并打乱题目
function organizeQuestions(questions: Question[]): { questions: Question[]; contextBreakpoints: Map<number, ContextType> } {
  // 分离单维度题和交叉验证题
  const singleQuestions = questions.filter((q): q is SingleQuestion => q.type === 'single')
  const cvQuestions = questions.filter((q): q is CrossValidationQuestion => q.type === 'cross_validation')

  // 按情境分组单维度题
  const byContext: Record<ContextType, SingleQuestion[]> = {
    natural: [],
    work: [],
    stress: [],
    mixed: []
  }

  for (const q of singleQuestions) {
    byContext[q.context].push(q)
  }

  // 打乱每个情境内的题目
  const shuffledNatural = shuffleArray(byContext.natural)
  const shuffledWork = shuffleArray(byContext.work)
  const shuffledStress = shuffleArray(byContext.stress)
  const shuffledCV = shuffleArray(cvQuestions)

  // 组合题目顺序：自然 → 工作 → 应激 → 交叉验证
  const organizedQuestions: Question[] = [
    ...shuffledNatural,
    ...shuffledWork,
    ...shuffledStress,
    ...shuffledCV
  ]

  // 记录情境切换点
  const contextBreakpoints = new Map<number, ContextType>()
  contextBreakpoints.set(0, 'natural')
  if (shuffledNatural.length > 0) {
    contextBreakpoints.set(shuffledNatural.length, 'work')
  }
  if (shuffledWork.length > 0) {
    contextBreakpoints.set(shuffledNatural.length + shuffledWork.length, 'stress')
  }
  if (shuffledStress.length > 0 && shuffledCV.length > 0) {
    contextBreakpoints.set(shuffledNatural.length + shuffledWork.length + shuffledStress.length, 'mixed')
  }

  return { questions: organizedQuestions, contextBreakpoints }
}

function App() {
  const [config, setConfig] = useState<QuestionnaireConfig | null>(null)
  const [phase, setPhase] = useState<PagePhase>('framework')
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState<Answer[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentContext, setCurrentContext] = useState<ContextType>('natural')
  const [startTime, setStartTime] = useState<number>(0)
  const [personalInfo, setPersonalInfo] = useState<PersonalInfo>({
    gender: '',
    ageGroup: '',
    currentStatus: '',
    industryField: ''
  })
  const [result, setResult] = useState<TestResult | null>(null)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [savedResult, setSavedResult] = useState<SaveResultResponse | null>(null)
  const [showResumeDialog, setShowResumeDialog] = useState(false)
  const [pendingProgress, setPendingProgress] = useState<SavedProgress | null>(null)

  // 组织题目（打乱并按情境排序）
  const { questions: organizedQuestions, contextBreakpoints } = useMemo(() => {
    if (!config) return { questions: [], contextBreakpoints: new Map<number, ContextType>() }
    return organizeQuestions(config.questions)
  }, [config])

  // 加载问卷配置
  useEffect(() => {
    async function loadQuestionnaire() {
      try {
        const response = await fetch('/questionnaire-v2.json')
        if (!response.ok) {
          throw new Error('Failed to load questionnaire')
        }
        const data: QuestionnaireConfig = await response.json()
        setConfig(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        setLoading(false)
      }
    }
    loadQuestionnaire()
  }, [])

  // 检查是否有保存的进度
  useEffect(() => {
    if (!loading && config) {
      const saved = loadProgress()
      if (saved && saved.answers.length > 0) {
        setPendingProgress(saved)
        setShowResumeDialog(true)
      }
    }
  }, [loading, config])

  // 添加退出确认
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (phase === 'question' || phase === 'personal_info' || phase === 'context_transition') {
        e.preventDefault()
        e.returnValue = '你有未完成的测试，确定离开吗？'
        return e.returnValue
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [phase])

  // 保存进度到 localStorage
  const saveCurrentProgress = useCallback(() => {
    if (phase === 'question' || phase === 'personal_info' || phase === 'context_transition') {
      saveProgress({
        personalInfo: personalInfo.gender ? personalInfo : null,
        answers,
        currentIndex: currentQuestionIndex,
        startTime,
        lastUpdated: Date.now(),
      })
    }
  }, [phase, personalInfo, answers, currentQuestionIndex, startTime])

  // 答题时自动保存
  useEffect(() => {
    saveCurrentProgress()
  }, [answers, currentQuestionIndex, personalInfo, saveCurrentProgress])

  // 恢复进度
  const handleResumeProgress = () => {
    if (pendingProgress) {
      if (pendingProgress.personalInfo) {
        setPersonalInfo(pendingProgress.personalInfo)
      }
      setAnswers(pendingProgress.answers)
      setCurrentQuestionIndex(pendingProgress.currentIndex)
      setStartTime(pendingProgress.startTime)

      // 根据进度决定进入哪个页面
      if (pendingProgress.answers.length > 0) {
        // 找到对应的情境
        for (const [index, context] of contextBreakpoints.entries()) {
          if (pendingProgress.currentIndex >= index) {
            setCurrentContext(context)
          }
        }
        setPhase('question')
      } else if (pendingProgress.personalInfo) {
        setPhase('personal_info')
      }
    }
    setShowResumeDialog(false)
    setPendingProgress(null)
  }

  // 放弃进度，重新开始
  const handleDiscardProgress = () => {
    clearProgress()
    setShowResumeDialog(false)
    setPendingProgress(null)
    setPhase('framework')  // 确保回到框架介绍页
  }

  // 从框架介绍进入开始测试页
  const handleStartFromFramework = () => {
    setPhase('intro')
  }

  // 开始测评 - 进入个人信息页
  const handleStart = () => {
    setStartTime(Date.now())
    setPhase('personal_info')
  }

  // 个人信息提交后开始正式答题
  const handlePersonalInfoSubmit = () => {
    if (!isPersonalInfoValid) return
    setPhase('context_transition')
    setCurrentContext('natural')
  }

  // 检查必填项是否完成
  const isPersonalInfoValid = personalInfo.gender && personalInfo.ageGroup && personalInfo.currentStatus

  // 继续到问题
  const handleContinueToQuestions = () => {
    setPhase('question')
  }

  // 获取当前题目的已有答案
  const getCurrentAnswer = (): Answer | undefined => {
    const currentQuestion = organizedQuestions[currentQuestionIndex]
    if (!currentQuestion) return undefined
    return answers.find(a => a.questionId === currentQuestion.id)
  }

  // 处理单维度题答案选择
  const handleSingleAnswer = (selected: 'A' | 'B') => {
    const currentQuestion = organizedQuestions[currentQuestionIndex]
    const answer: Answer = {
      questionId: currentQuestion.id,
      selected,
      timestamp: Date.now(),
    }

    // 更新或添加答案
    const existingIndex = answers.findIndex(a => a.questionId === currentQuestion.id)
    if (existingIndex >= 0) {
      const newAnswers = [...answers]
      newAnswers[existingIndex] = answer
      setAnswers(newAnswers)
    } else {
      setAnswers([...answers, answer])
    }

    // 自动跳转下一题（非最后一题时）
    const isLastQuestion = currentQuestionIndex >= organizedQuestions.length - 1
    if (!isLastQuestion) {
      const nextIndex = currentQuestionIndex + 1
      setTimeout(() => {
        // 检查是否需要显示情境过渡页
        if (contextBreakpoints.has(nextIndex)) {
          const nextContext = contextBreakpoints.get(nextIndex)!
          setCurrentContext(nextContext)
          setCurrentQuestionIndex(nextIndex)
          setPhase('context_transition')
        } else {
          setCurrentQuestionIndex(prev => prev + 1)
        }
      }, 500)
    }
  }

  // 处理交叉验证题答案选择
  const handleCVAnswer = (optionId: string) => {
    const currentQuestion = organizedQuestions[currentQuestionIndex]
    const answer: Answer = {
      questionId: currentQuestion.id,
      selected: optionId,
      timestamp: Date.now(),
    }

    // 更新或添加答案
    const existingIndex = answers.findIndex(a => a.questionId === currentQuestion.id)
    if (existingIndex >= 0) {
      const newAnswers = [...answers]
      newAnswers[existingIndex] = answer
      setAnswers(newAnswers)
    } else {
      setAnswers([...answers, answer])
    }

    // 自动跳转下一题（非最后一题时）
    const isLastQuestion = currentQuestionIndex >= organizedQuestions.length - 1
    if (!isLastQuestion) {
      const nextIndex = currentQuestionIndex + 1
      setTimeout(() => {
        // 检查是否需要显示情境过渡页
        if (contextBreakpoints.has(nextIndex)) {
          const nextContext = contextBreakpoints.get(nextIndex)!
          setCurrentContext(nextContext)
          setCurrentQuestionIndex(nextIndex)
          setPhase('context_transition')
        } else {
          setCurrentQuestionIndex(prev => prev + 1)
        }
      }, 500)
    }
  }

  // 下一题
  const handleNextQuestion = async () => {
    const currentAnswer = getCurrentAnswer()
    if (!currentAnswer) return // 未作答不能下一题

    const nextIndex = currentQuestionIndex + 1

    if (nextIndex >= organizedQuestions.length) {
      // 问卷完成，计算结果
      await handleSubmit()
      return
    }

    // 检查是否需要显示情境过渡页
    if (contextBreakpoints.has(nextIndex)) {
      const nextContext = contextBreakpoints.get(nextIndex)!
      setCurrentContext(nextContext)
      setCurrentQuestionIndex(nextIndex)
      setPhase('context_transition')
    } else {
      setCurrentQuestionIndex(nextIndex)
    }
  }

  // 上一题
  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      // 检查是否需要回到情境过渡页
      const prevIndex = currentQuestionIndex - 1

      // 检查是否在当前情境的第一题（需要回到过渡页）
      if (contextBreakpoints.has(currentQuestionIndex)) {
        // 当前是情境的第一题，回到上一个情境的最后一题
        setCurrentQuestionIndex(prevIndex)
      } else {
        setCurrentQuestionIndex(prevIndex)
      }
    } else {
      // 第一题时返回数据收集页
      setPhase('personal_info')
    }
  }

  // 提交并计算结果
  const handleSubmit = async () => {
    const duration = Math.round((Date.now() - startTime) / 1000)
    const testResult = calculateResult(
      answers,
      config!,
      `response-${Date.now()}`,
      duration,
      personalInfo.gender ? `${personalInfo.gender}-${personalInfo.ageGroup}-${personalInfo.currentStatus}` : undefined
    )
    setResult(testResult)
    setPhase('result')

    // 清除保存的进度
    clearProgress()

    // 自动保存到数据库
    setSaveStatus('saving')
    try {
      const response = await saveResult({
        personalInfo: {
          gender: personalInfo.gender,
          ageGroup: personalInfo.ageGroup,
          currentStatus: personalInfo.currentStatus,
          workField: personalInfo.industryField || undefined,
        },
        durationSeconds: duration,
        result: {
          modes: testResult.modes,
          dimensions: testResult.dimensions.map(dim => ({
            dimensionId: dim.dimensionId,
            stability: dim.stability,
            natural: {
              resultLetter: dim.natural.resultLetter,
              poleACount: dim.natural.poleACount,
              poleBCount: dim.natural.poleBCount,
              isWeak: dim.natural.isWeak,
            },
            work: {
              resultLetter: dim.work.resultLetter,
              poleACount: dim.work.poleACount,
              poleBCount: dim.work.poleBCount,
              isWeak: dim.work.isWeak,
            },
            stress: {
              resultLetter: dim.stress.resultLetter,
              poleACount: dim.stress.poleACount,
              poleBCount: dim.stress.poleBCount,
              isWeak: dim.stress.isWeak,
            },
          })),
          overallStability: {
            type: testResult.overallStability,
            stableAxes: testResult.stableAxes,
            flexibleAxes: testResult.flexibleAxes.map(f => ({
              dimension: f.dimension,
              pattern: f.pattern,
            })),
          },
          crossValidation: {
            rate: testResult.crossValidation.rate,
            reliable: testResult.crossValidation.reliable,
            totalChecks: testResult.crossValidation.summary.totalChecks,
            consistentChecks: testResult.crossValidation.summary.consistentChecks,
          },
        },
      })

      if (response.success && response.data) {
        setSavedResult(response.data)
        setSaveStatus('saved')
      } else {
        console.error('Save failed:', response.error)
        setSaveStatus('error')
      }
    } catch (error) {
      console.error('Save error:', error)
      setSaveStatus('error')
    }
  }

  // 重新开始
  const handleRestart = () => {
    setPhase('framework')
    setCurrentQuestionIndex(0)
    setAnswers([])
    setCurrentContext('natural')
    setStartTime(0)
    setPersonalInfo({
      gender: '',
      ageGroup: '',
      currentStatus: '',
      industryField: ''
    })
    setResult(null)
    setSaveStatus('idle')
    setSavedResult(null)
    clearProgress()
  }

  // Loading 状态
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">加载问卷中...</p>
        </div>
      </div>
    )
  }

  // 错误状态
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">加载失败: {error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            重试
          </button>
        </div>
      </div>
    )
  }

  if (!config || organizedQuestions.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <p className="text-gray-600">暂无题目</p>
      </div>
    )
  }

  // 恢复进度对话框
  if (showResumeDialog && pendingProgress) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl border border-blue-100 p-6 max-w-md w-full shadow-lg">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">发现未完成的测试</h2>
          <p className="text-gray-600 mb-2">
            你有一份未完成的测试，已完成 {pendingProgress.answers.length} / {organizedQuestions.length} 题。
          </p>
          <p className="text-gray-500 text-sm mb-6">
            上次答题时间：{new Date(pendingProgress.lastUpdated).toLocaleString('zh-CN')}
          </p>
          <div className="flex gap-3">
            <button
              onClick={handleDiscardProgress}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              重新开始
            </button>
            <button
              onClick={handleResumeProgress}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              继续答题
            </button>
          </div>
        </div>
      </div>
    )
  }

  // 框架介绍页面
  if (phase === 'framework') {
    return <FrameworkIntro onStart={handleStartFromFramework} />
  }

  // 介绍页面
  if (phase === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                  认知风格测评
                </h1>
                <p className="text-lg text-gray-600">
                  探索您独特的思维模式与认知偏好
                </p>
              </div>

              <div className="space-y-4 mb-8 text-gray-700">
                <p>
                  本测评将从<strong>四个核心维度</strong>分析您的认知风格，帮助您更好地了解自己在不同情境下的思维方式和行为偏好。
                </p>
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-2">测评说明</h3>
                  <ul className="space-y-2 text-blue-800 text-sm">
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      共 {organizedQuestions.length} 道题目，预计需要 10-15 分钟
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      题目将分为三个情境：自然状态、工作状态、应激状态
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      请根据您的<strong>真实感受</strong>选择更符合您想法的选项
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      没有对错之分，诚实作答能获得更准确的结果
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      答案会自动保存，中途离开可以继续
                    </li>
                  </ul>
                </div>
              </div>

              <button
                onClick={handleStart}
                className="w-full py-4 bg-blue-600 text-white text-lg font-semibold rounded-xl hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl"
              >
                开始测评
              </button>
            </div>
          </div>
        </div>

        {/* 版权声明 */}
        <footer className="text-center text-xs text-gray-400 py-6">
          <p>© 2024 MindType</p>
          <p className="mt-1">内容受版权保护，未经授权禁止商业使用</p>
        </footer>
      </div>
    )
  }

  // 个人信息收集页面
  if (phase === 'personal_info') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-6 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">基本信息</h2>
              <p className="text-gray-600 text-sm">以下信息仅用于研究分析，不会关联到个人身份。带 <span className="text-red-500">*</span> 为必填项。</p>
            </div>

            <div className="space-y-6">
              {/* 性别 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  性别 <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {[
                    { value: 'male', label: '男' },
                    { value: 'female', label: '女' },
                    { value: 'other', label: '其他 / 不愿透露' }
                  ].map(option => (
                    <label key={option.value} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="gender"
                        value={option.value}
                        checked={personalInfo.gender === option.value}
                        onChange={(e) => setPersonalInfo(prev => ({ ...prev, gender: e.target.value }))}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="ml-3 text-gray-700">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* 年龄段 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  年龄段 <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {[
                    { value: 'under18', label: '18岁以下' },
                    { value: '18-22', label: '18-22岁' },
                    { value: '23-25', label: '23-25岁' },
                    { value: '26-30', label: '26-30岁' },
                    { value: '31-35', label: '31-35岁' },
                    { value: 'over36', label: '36岁以上' }
                  ].map(option => (
                    <label key={option.value} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="ageGroup"
                        value={option.value}
                        checked={personalInfo.ageGroup === option.value}
                        onChange={(e) => setPersonalInfo(prev => ({ ...prev, ageGroup: e.target.value }))}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="ml-3 text-gray-700">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* 当前状态 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  当前状态 <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {[
                    { value: 'student_no_exp', label: '在校学生（无实习/兼职经验）' },
                    { value: 'student_with_exp', label: '在校学生（有实习或兼职经验）' },
                    { value: 'work_under1', label: '已工作，1年以内' },
                    { value: 'work_1to3', label: '已工作，1-3年' },
                    { value: 'work_3to5', label: '已工作，3-5年' },
                    { value: 'work_over5', label: '已工作，5年以上' },
                    { value: 'freelance', label: '自由职业 / 创业' },
                    { value: 'other_status', label: '其他' }
                  ].map(option => (
                    <label key={option.value} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="currentStatus"
                        value={option.value}
                        checked={personalInfo.currentStatus === option.value}
                        onChange={(e) => setPersonalInfo(prev => ({ ...prev, currentStatus: e.target.value }))}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="ml-3 text-gray-700">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* 职业领域 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  职业领域 <span className="text-gray-400 text-xs font-normal">（选填）</span>
                </label>
                <div className="space-y-2">
                  {[
                    { value: 'tech', label: '互联网 / 科技' },
                    { value: 'finance', label: '金融 / 咨询' },
                    { value: 'manufacturing', label: '制造 / 工程' },
                    { value: 'education', label: '教育 / 科研' },
                    { value: 'healthcare', label: '医疗 / 健康' },
                    { value: 'government', label: '政府 / 事业单位' },
                    { value: 'creative', label: '文化 / 创意 / 媒体' },
                    { value: 'sales', label: '销售 / 服务' },
                    { value: 'student', label: '学生' },
                    { value: 'other_industry', label: '其他' }
                  ].map(option => (
                    <label key={option.value} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="industryField"
                        value={option.value}
                        checked={personalInfo.industryField === option.value}
                        onChange={(e) => setPersonalInfo(prev => ({ ...prev, industryField: e.target.value }))}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="ml-3 text-gray-700">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {!isPersonalInfoValid && (
              <p className="text-sm text-amber-600 mt-6">
                请完成所有必填项后继续
              </p>
            )}

            <button
              onClick={handlePersonalInfoSubmit}
              disabled={!isPersonalInfoValid}
              className={`w-full mt-8 py-4 text-lg font-semibold rounded-xl transition-colors shadow-lg ${
                isPersonalInfoValid
                  ? 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-xl'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              继续
            </button>
          </div>
        </div>

        {/* 版权声明 */}
        <footer className="text-center text-xs text-gray-400 py-6 mt-8">
          <p>© 2024 MindType</p>
          <p className="mt-1">内容受版权保护，未经授权禁止商业使用</p>
        </footer>
      </div>
    )
  }

  // 情境过渡页面
  if (phase === 'context_transition') {
    const contextInfo = CONTEXT_INFO[currentContext]
    const completedCount = currentQuestionIndex
    const totalCount = organizedQuestions.length

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="max-w-xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10">
              {/* 进度指示 */}
              {completedCount > 0 && (
                <div className="mb-6">
                  <div className="flex justify-between text-sm text-gray-500 mb-2">
                    <span>整体进度</span>
                    <span>{completedCount} / {totalCount}</span>
                  </div>
                  <div className="w-full bg-blue-100 rounded-full h-1.5">
                    <div
                      className="bg-blue-500 h-1.5 rounded-full transition-all duration-500"
                      style={{ width: `${(completedCount / totalCount) * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full mx-auto mb-6 flex items-center justify-center">
                  <span className="text-2xl font-semibold text-blue-600">
                    {currentContext === 'natural' ? '1' : currentContext === 'work' ? '2' : currentContext === 'stress' ? '3' : '4'}
                  </span>
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  {contextInfo.name}
                </h2>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  {contextInfo.description}
                </p>

                <button
                  onClick={handleContinueToQuestions}
                  className="px-8 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl"
                >
                  开始作答
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 版权声明 */}
        <footer className="text-center text-xs text-gray-400 py-6">
          <p>© 2024 MindType</p>
          <p className="mt-1">内容受版权保护，未经授权禁止商业使用</p>
        </footer>
      </div>
    )
  }

  // 结果页面
  if (phase === 'result' && result) {
    return (
      <ResultPage
        result={result}
        onRestart={handleRestart}
        saveStatus={saveStatus}
        savedResult={savedResult}
      />
    )
  }

  // 完成页面（备用，正常流程会直接跳到 result）
  if (phase === 'complete') {
    const duration = Math.round((Date.now() - startTime) / 1000)
    const minutes = Math.floor(duration / 60)
    const seconds = duration % 60

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mb-4">测评完成</h2>
              <p className="text-gray-600 mb-6">
                您已完成全部 {organizedQuestions.length} 道题目
              </p>

              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <p className="text-3xl font-bold text-blue-600">{answers.length}</p>
                    <p className="text-sm text-gray-500">答题数</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-blue-600">
                      {minutes > 0 ? `${minutes}分${seconds}秒` : `${seconds}秒`}
                    </p>
                    <p className="text-sm text-gray-500">用时</p>
                  </div>
                </div>
              </div>

              <p className="text-gray-500 text-sm mb-6">
                测评结果正在生成中，请稍候...
              </p>

              <button
                onClick={handleRestart}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                重新测评
              </button>
            </div>
          </div>
        </div>

        {/* 版权声明 */}
        <footer className="text-center text-xs text-gray-400 py-6">
          <p>© 2024 MindType</p>
          <p className="mt-1">内容受版权保护，未经授权禁止商业使用</p>
        </footer>
      </div>
    )
  }

  // 问题页面
  const currentQuestion = organizedQuestions[currentQuestionIndex]
  const currentAnswer = getCurrentAnswer()
  const isLastQuestion = currentQuestionIndex >= organizedQuestions.length - 1

  // 手动点击下一题（不同于自动跳转，需要处理情境过渡）
  const handleManualNext = () => {
    if (!currentAnswer) return
    const nextIndex = currentQuestionIndex + 1
    if (contextBreakpoints.has(nextIndex)) {
      const nextContext = contextBreakpoints.get(nextIndex)!
      setCurrentContext(nextContext)
      setCurrentQuestionIndex(nextIndex)
      setPhase('context_transition')
    } else {
      setCurrentQuestionIndex(prev => prev + 1)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4">
      <div className="max-w-4xl mx-auto relative">
        {/* 顶部进度条 */}
        <div className="mb-6">
          <SegmentedProgress
            currentIndex={currentQuestionIndex}
            totalQuestions={organizedQuestions.length}
            contextBreakpoints={contextBreakpoints}
          />
        </div>

        {/* 上一题按钮 - 桌面端左侧 */}
        <button
          onClick={handlePrevQuestion}
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-16 w-12 h-12 bg-white border border-gray-200 rounded-full hidden md:flex items-center justify-center text-gray-500 hover:bg-gray-50 hover:border-blue-300 hover:text-blue-500 transition-colors shadow-sm"
          title="上一题"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* 下一题按钮 - 桌面端右侧（非最后一题时显示） */}
        {!isLastQuestion && (
          <button
            onClick={handleManualNext}
            disabled={!currentAnswer}
            className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-16 w-12 h-12 rounded-full hidden md:flex items-center justify-center transition-colors shadow-sm ${
              currentAnswer
                ? 'bg-white border border-gray-200 text-gray-500 hover:bg-gray-50 hover:border-blue-300 hover:text-blue-500'
                : 'bg-gray-100 border border-gray-200 text-gray-300 cursor-not-allowed'
            }`}
            title={currentAnswer ? "下一题" : "请先选择一个选项"}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}

        {/* 问题卡片 */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          {/* 情境描述 */}
          <div className="mb-6">
            <h2 className="text-lg md:text-xl font-medium text-gray-900 leading-relaxed">
              {currentQuestion.scenario}
            </h2>
          </div>

          {/* 选项 - 根据题目类型渲染 */}
          {currentQuestion.type === 'single' ? (
            // 单维度题：两列选项
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => handleSingleAnswer('A')}
                className={`group p-5 border-2 rounded-xl transition-all duration-200 text-left ${
                  currentAnswer?.selected === 'A'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                }`}
              >
                <ul className="space-y-2">
                  {currentQuestion.optionA.choices.map((choice, index) => (
                    <li key={index} className="text-gray-700 flex items-start text-sm md:text-base">
                      <span className={`mr-2 ${currentAnswer?.selected === 'A' ? 'text-blue-500' : 'text-gray-400 group-hover:text-blue-500'}`}>•</span>
                      {choice}
                    </li>
                  ))}
                </ul>
              </button>

              <button
                onClick={() => handleSingleAnswer('B')}
                className={`group p-5 border-2 rounded-xl transition-all duration-200 text-left ${
                  currentAnswer?.selected === 'B'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                }`}
              >
                <ul className="space-y-2">
                  {currentQuestion.optionB.choices.map((choice, index) => (
                    <li key={index} className="text-gray-700 flex items-start text-sm md:text-base">
                      <span className={`mr-2 ${currentAnswer?.selected === 'B' ? 'text-blue-500' : 'text-gray-400 group-hover:text-blue-500'}`}>•</span>
                      {choice}
                    </li>
                  ))}
                </ul>
              </button>
            </div>
          ) : (
            // 交叉验证题：单列多选项
            <div className="space-y-3">
              {currentQuestion.options.map((option, index) => (
                <button
                  key={option.id}
                  onClick={() => handleCVAnswer(option.id)}
                  className={`group w-full p-5 border-2 rounded-xl transition-all duration-200 text-left ${
                    currentAnswer?.selected === option.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                  }`}
                >
                  <div className="flex items-start">
                    <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-medium mr-4 ${
                      currentAnswer?.selected === option.id
                        ? 'bg-blue-100 text-blue-600'
                        : 'bg-gray-100 group-hover:bg-blue-100 text-gray-600 group-hover:text-blue-600'
                    }`}>
                      {String.fromCharCode(65 + index)}
                    </span>
                    <ul className="space-y-1.5 flex-1">
                      {option.choices.map((choice, choiceIndex) => (
                        <li key={choiceIndex} className="text-gray-700 text-sm md:text-base">
                          {choice}
                        </li>
                      ))}
                    </ul>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* 底部提示和提交按钮 */}
          <div className="mt-8">
            <p className={`text-center text-sm mb-4 ${currentAnswer ? 'text-gray-500' : 'text-orange-500'}`}>
              {currentAnswer ? '选择更符合您真实想法的选项' : '请选择一个选项后继续'}
            </p>

            {/* 最后一题显示提交按钮 */}
            {isLastQuestion && (
              <button
                onClick={handleNextQuestion}
                disabled={!currentAnswer}
                className={`w-full px-6 py-3 font-medium rounded-xl transition-colors ${
                  currentAnswer
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                提交
              </button>
            )}
          </div>
        </div>

        {/* 移动端导航按钮 */}
        <div className="mt-4 flex justify-center gap-4 md:hidden">
          {/* 上一题 */}
          <button
            onClick={handlePrevQuestion}
            className="w-12 h-12 bg-white border border-gray-200 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-50 hover:border-blue-300 hover:text-blue-500 transition-colors shadow-sm"
            title="上一题"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* 下一题（非最后一题时显示） */}
          {!isLastQuestion && (
            <button
              onClick={handleManualNext}
              disabled={!currentAnswer}
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors shadow-sm ${
                currentAnswer
                  ? 'bg-white border border-gray-200 text-gray-500 hover:bg-gray-50 hover:border-blue-300 hover:text-blue-500'
                  : 'bg-gray-100 border border-gray-200 text-gray-300 cursor-not-allowed'
              }`}
              title={currentAnswer ? "下一题" : "请先选择一个选项"}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* 版权声明 */}
      <footer className="text-center text-xs text-gray-400 py-6 border-t border-gray-100 mt-8">
        <p>© 2024 MindType</p>
        <p className="mt-1">内容受版权保护，未经授权禁止商业使用</p>
      </footer>
    </div>
  )
}

export default App
